import { lerUnidade, rotuloDaUnidade, type Unidade } from '@viewer/lib/unidades'
import { quantityClassFor } from './quantityRules'

/** As unidades das quantidades do BoQ.
 *
 *  A geometria que o web-ifc entrega está nas unidades de comprimento do
 *  ficheiro (um modelo em mm dá vértices em mm). O comprimento calculado
 *  fica nessa unidade; a área e o volume saem em mm² e mm³ e têm de ir
 *  para a unidade de área e de volume que o projecto declara (num
 *  modelo do Revit ou do Tekla em mm, m² e m³). Uma IfcQuantityArea sem
 *  unidade própria vale na unidade de área do projecto: escrever mm²
 *  sem converter dava 54 600 000 m² a uma parede.
 *
 *  Nunca se assume SI: sem a unidade declarada (ou com duas do mesmo
 *  tipo), a quantidade fica "unidade não declarada" e não se escreve
 *  sem confirmação. */

export type GeneroDaQuantidade = 'comprimento' | 'area' | 'volume' | 'massa' | 'contagem' | 'tempo'

export interface UnidadeConhecida {
  unidade: Unidade
  /** Quanto vale uma unidade em SI (m, m², m³, kg). Null quando não se
   *  sabe (uma unidade de contexto, uma derivada). */
  factorSI: number | null
}

export interface UnidadesBoq {
  /** Por tipo (LENGTHUNIT, AREAUNIT, VOLUMEUNIT, MASSUNIT): a unidade, ou
   *  'ambigua' quando o projecto declara mais de uma, ou ausente. */
  porTipo: Map<string, UnidadeConhecida | 'ambigua'>
}

const TIPO_DO_GENERO: Record<GeneroDaQuantidade, string | null> = {
  comprimento: 'LENGTHUNIT',
  area: 'AREAUNIT',
  volume: 'VOLUMEUNIT',
  massa: 'MASSUNIT',
  contagem: null,
  tempo: 'TIMEUNIT',
}

export function generoDaQuantidade(nome: string): GeneroDaQuantidade {
  switch (quantityClassFor(nome)) {
    case 'IfcQuantityArea':
      return 'area'
    case 'IfcQuantityVolume':
      return 'volume'
    case 'IfcQuantityWeight':
      return 'massa'
    case 'IfcQuantityCount':
      return 'contagem'
    case 'IfcQuantityTime':
      return 'tempo'
    default:
      return 'comprimento'
  }
}

/** O valor de um atributo do web-ifc: cru, { value }, ou a medida com
 *  _representationValue / _internalValue. */
function valor(x: unknown): unknown {
  if (!x || typeof x !== 'object') return x
  const o = x as Record<string, unknown>
  if ('value' in o) return o.value
  if ('_representationValue' in o) return o._representationValue
  if ('_internalValue' in o) return o._internalValue
  return x
}

const EXPOENTE_DO_PREFIXO: Record<string, number> = {
  EXA: 18, PETA: 15, TERA: 12, GIGA: 9, MEGA: 6, KILO: 3, HECTO: 2, DECA: 1,
  DECI: -1, CENTI: -2, MILLI: -3, MICRO: -6, NANO: -9, PICO: -12, FEMTO: -15, ATTO: -18,
}

/** Quanto vale a unidade em SI, a partir da entidade do web-ifc. O
 *  prefixo de uma área ou de um volume aplica-se ao metro: mm² é
 *  (1e-3 m)². */
export function factorSI(e: unknown, fundo = 0): number | null {
  if (!e || typeof e !== 'object' || fundo > 5) return null
  const o = e as Record<string, unknown>
  const classe = String(o.type ?? '').toUpperCase()
  if (classe === 'IFCSIUNIT') {
    const nome = String(valor(o.Name) ?? '')
    const prefixo = String(valor(o.Prefix) ?? '')
    const p = prefixo ? EXPOENTE_DO_PREFIXO[prefixo] : 0
    if (p === undefined) return null
    const base: Record<string, [number, number]> = {
      METRE: [1, 1],
      SQUARE_METRE: [1, 2],
      CUBIC_METRE: [1, 3],
      GRAM: [1e-3, 1],
      SECOND: [1, 1],
    }
    const b = base[nome]
    if (!b) return null
    return b[0] * 10 ** (p * b[1])
  }
  if (classe === 'IFCCONVERSIONBASEDUNIT' || classe === 'IFCCONVERSIONBASEDUNITWITHOFFSET') {
    const f = o.ConversionFactor as Record<string, unknown> | undefined
    const v = Number(valor(f?.ValueComponent))
    const b = factorSI(f?.UnitComponent, fundo + 1)
    return Number.isFinite(v) && b !== null ? v * b : null
  }
  return null
}

/** As unidades do projecto que o BoQ usa, a partir das entidades do
 *  IfcUnitAssignment (como o web-ifc as dá, referências resolvidas). */
export function lerUnidadesBoq(entidades: unknown[]): UnidadesBoq {
  const porTipo = new Map<string, UnidadeConhecida | 'ambigua'>()
  for (const e of entidades) {
    const u = lerUnidade(e)
    if (!u) continue
    if (porTipo.has(u.tipo)) porTipo.set(u.tipo, 'ambigua')
    else porTipo.set(u.tipo, { unidade: u, factorSI: factorSI(e) })
  }
  return { porTipo }
}

export interface UnidadeDaColuna {
  genero: GeneroDaQuantidade
  /** A unidade em que o valor está, quando o projecto a declara. */
  unidade: Unidade | null
  /** "m³ (#13)", "unidade não declarada", "unidade ambígua", ou "" numa
   *  contagem. */
  rotulo: string
  /** true quando o valor está numa unidade que o projecto declara: pode
   *  escrever-se sem unidade própria. */
  declarada: boolean
}

function conhecida(u: UnidadesBoq, tipo: string | null): UnidadeConhecida | 'ambigua' | null {
  if (!tipo) return null
  return u.porTipo.get(tipo) ?? null
}

/** A unidade em que fica cada quantidade calculada. */
export function unidadeDaColuna(nome: string, u: UnidadesBoq): UnidadeDaColuna {
  const genero = generoDaQuantidade(nome)
  if (genero === 'contagem') return { genero, unidade: null, rotulo: '', declarada: true }
  const alvo = conhecida(u, TIPO_DO_GENERO[genero])
  const comp = conhecida(u, 'LENGTHUNIT')
  if (alvo === 'ambigua') return { genero, unidade: null, rotulo: 'unidade ambígua', declarada: false }
  if (!alvo) return { genero, unidade: null, rotulo: 'unidade não declarada', declarada: false }
  // A área e o volume calculam-se do comprimento: sem o factor dos dois
  // não se converte.
  if (genero === 'area' || genero === 'volume') {
    if (!comp || comp === 'ambigua' || comp.factorSI === null || alvo.factorSI === null) {
      return { genero, unidade: null, rotulo: 'unidade não declarada', declarada: false }
    }
  }
  return {
    genero,
    unidade: alvo.unidade,
    rotulo: rotuloDaUnidade({ estado: 'projecto', unidade: alvo.unidade, tipo: alvo.unidade.tipo }) ?? alvo.unidade.simbolo,
    declarada: true,
  }
}

/** Passa os valores calculados (comprimento na unidade do ficheiro, área
 *  e volume no quadrado e no cubo dela) para as unidades do projecto. Os
 *  que não têm unidade declarada ficam como foram calculados. */
export function paraUnidadesDoProjecto(
  calculado: Record<string, number>,
  u: UnidadesBoq,
): Record<string, number> {
  const comp = conhecida(u, 'LENGTHUNIT')
  const fL = comp && comp !== 'ambigua' ? comp.factorSI : null
  const out: Record<string, number> = {}
  for (const [nome, v] of Object.entries(calculado)) {
    const col = unidadeDaColuna(nome, u)
    if (!col.declarada || fL === null || (col.genero !== 'area' && col.genero !== 'volume')) {
      out[nome] = v
      continue
    }
    const alvo = conhecida(u, col.genero === 'area' ? 'AREAUNIT' : 'VOLUMEUNIT') as UnidadeConhecida
    const n = col.genero === 'area' ? 2 : 3
    // 12 algarismos: tira o ruído da conversão (54.599999999999994) sem
    // perder nada que a geometria triangulada garanta.
    out[nome] = Number(((v * fL ** n) / alvo.factorSI!).toPrecision(12))
  }
  return out
}

/** Uma quantidade escrita pelo BoQ, para o painel de propriedades: o
 *  valor, a unidade em que ficou no ficheiro e a marca de calculado.
 *  "7.2 m³ (#13) (calculado)"; sem unidade declarada,
 *  "7200000000 (unidade não declarada, calculado)". */
export function textoDaQuantidadeEscrita(valor: number, rotulo: string): string {
  const n = String(Number(valor.toFixed(4)))
  if (!rotulo) return `${n} (calculado)`
  if (rotulo.startsWith('unidade')) return `${n} (${rotulo}, calculado)`
  return `${n} ${rotulo} (calculado)`
}
